import { Suspense, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'
import Features from '../../components/Features'
import FrequentlyAskedQuestions from '../../components/FrequentlyAskedQuestions'
import LimitedTimeOfferBanner from '../../components/LimitedTimeOfferBanner'
import LimitedTimeOfferDialog from '../../components/LimitedTimeOfferDialog'
import MoneyBackGuarantee from '../../components/MoneyBackGuarantee'
import Plans from '../../components/Plans'
import Trust from '../../components/Trust'
import { GetPricesData } from '../../loaders/getPricesLoader'
import { Plan } from '../../types'
import ErrorPage from '../ErrorPage'
import LoadingPage from '../LoadingPage'

export default function PlansPage() {
  const [countdownUntil, setCountdownUntil] = useState<number>(0)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('first')

  const data = useLoaderData() as GetPricesData

  useEffect(() => {
    const dialogTimeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, 30000)

    setCountdownUntil(Date.now() + 9 * 60 * 1000)

    return () => {
      clearTimeout(dialogTimeout)
    }
  }, [])

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(getPricesData: GetPricesData) => (
          <div>
            <LimitedTimeOfferBanner
              countdownUntil={countdownUntil}
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              plan={selectedPlan}
              priceId={
                selectedPlan === 'first'
                  ? getPricesData.firstIntroPrice?.id
                  : getPricesData.secondIntroPrice?.id
              }
              setIsSubmitting={setIsSubmitting}
              stripePriceFull={
                selectedPlan === 'first'
                  ? getPricesData.firstPrice
                  : getPricesData.secondPrice
              }
            />

            <main className="custom-container pt-8 pb-14">
              <h1 className="custom-heading mb-6">
                <Trans
                  components={{
                    colored: <span className="text-primary" />,
                  }}
                  i18nKey="pages.checkout.plans.heading"
                />
              </h1>

              <section className="mt-9 mb-16">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                />
              </section>

              <section className="mb-12">
                <Trust />
              </section>

              <section className="mb-12">
                <Features />
              </section>

              <section className="mb-12">
                <MoneyBackGuarantee />
              </section>

              <section className="mb-12">
                <FrequentlyAskedQuestions />
              </section>

              <section className="mb-16">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                />
              </section>
            </main>

            <LimitedTimeOfferDialog
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              open={isDialogOpen}
              plan={selectedPlan}
              setIsSubmitting={setIsSubmitting}
              stripePriceFull={
                selectedPlan === 'first'
                  ? getPricesData.firstPrice
                  : getPricesData.secondPrice
              }
              stripePriceIntro={
                selectedPlan === 'first'
                  ? getPricesData.firstIntroPrice
                  : getPricesData.secondIntroPrice
              }
            />
          </div>
        )}
      </Await>
    </Suspense>
  )
}
